import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { JobAcceptedResponse, PatchSlackConnectionRequest, SlackConnectionDto, SlackConnectionResponse, SlackDisconnectResponse } from '../../shared/api-types.js';
import { autoProcessWindowLabel } from '../../shared/fellow.js';
import { SLACK_CONNECT_PATH } from '../../shared/slack.js';
import { api } from '../api.js';
import { useMe } from '../shell/me-context.js';
import {
  Banner,
  Button,
  Checkbox,
  ConfirmDialog,
  EM_DASH,
  FailureView,
  Icon,
  LinkButton,
  RelativeTime,
  Section,
  StateView,
  StatusPill,
  failureDetail,
  inlineFailureText,
  useRequest,
  useToast,
} from '../ui/index.js';
import { maskedKey } from './lib/connection.js';
import { UnattendedStreakRow } from './connection-run-rows.js';
import { NEVER, connectionRunsPath, lastRunStatePresentation, runTriggerPresentation } from './lib/connection-runs.js';
import {
  SLACK_SETTINGS_LOCKED_REASON,
  autoCheckLabel,
  dailyLookNote,
  slackDisclosure,
  slackOutcomeNotice,
  slackPanelView,
  slackReadableSummary,
  slackStatusPresentation,
  sweepWithdrawalNote,
  type SlackNotice,
} from './lib/slack-connection.js';
import { SlackExclusionList } from './slack-exclusions.js';

// The Slack connection (ADR 0008, amended by ADR 0010; owner ruling 20260926-002133). Each person
// connects their own Slack account through Slack's consent page — a full navigation to
// /auth/slack/start, which ends back here with `?slack=<outcome>`. By default a check may read
// everything they can see in Slack (R7); they narrow it with "Channels Calis won't read" and the switch
// for direct messages and group chats, and may switch automatic checks off (R3, R4). Before connecting,
// the panel says what is read, how a check looks (R8: judged searches first, every conversation last),
// when, what leaves Calis — the judges' calls included — and what is kept. The token never reaches the
// page: a connection is shown by an eight-character fingerprint of its credential. The connected facts
// answer 06-modules §6's questions for the channel-list refresh (findings D5, S5): the State of the most
// recent refresh, the last success with "— never" where there has been none, how the last refresh
// began, the unattended streak, and the way to the recent refreshes (§6.3 R2).

const SYNC_POLL_MS = 4_000;
const SYNC_POLL_MAX = 45;

export interface SlackPanelProps {
  /** Whether this panel's "Connect …" offer is the page's one primary action (03-components §2). */
  connectIsPrimary: boolean;
}

export function SlackPanel({ connectIsPrimary }: SlackPanelProps) {
  const me = useMe();
  const toast = useToast();
  const request = useRequest<SlackConnectionResponse>((signal) => api.get<SlackConnectionResponse>('/api/connections/slack', { signal }), []);
  const data = request.data;
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  // Sent here from a meeting's Slack tab (`/connections#slack-connection`): the panel is brought into
  // view once it has drawn, since it sits below Fellow's and loads on its own.
  const sentToSlack = useRef(location.hash === '#slack-connection');
  // The outcome of a return from Slack: read once, then taken out of the address so a reload or a
  // shared link does not say it again. A banner stays until the person leaves the page.
  const [outcome] = useState(() => slackOutcomeNotice(params.get('slack')));
  const [outcomeDismissed, setOutcomeDismissed] = useState(false);
  const outcomeHandled = useRef(false);
  const [syncing, setSyncing] = useState(false);
  const [polls, setPolls] = useState(0);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  useEffect(() => {
    if (outcomeHandled.current || !params.has('slack')) return;
    outcomeHandled.current = true;
    const next = new URLSearchParams(params);
    next.delete('slack');
    setParams(next, { replace: true });
    if (outcome?.kind === 'toast') toast.success(outcome.message);
    // The person comes back from Slack to this panel, below Fellow's: bring it into view.
    document.getElementById('slack-connection')?.scrollIntoView({ block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sentToSlack.current || !data) return;
    sentToSlack.current = false;
    document.getElementById('slack-connection')?.scrollIntoView({ block: 'start' });
  }, [data]);

  // While the channel list refreshes, poll so the list and its state land without a manual reload.
  const inProgress = data?.connection?.syncInProgress === true;
  useEffect(() => {
    if (!inProgress || polls >= SYNC_POLL_MAX) return;
    const handle = window.setTimeout(() => {
      setPolls((count) => count + 1);
      request.reload();
    }, SYNC_POLL_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, polls, data?.connection?.lastSyncStartedAt]);

  const refreshList = async (): Promise<void> => {
    setSyncing(true);
    const result = await api.post<JobAcceptedResponse>('/api/connections/slack/sync');
    setSyncing(false);
    if (!result.ok) {
      toast.error(<>We couldn&rsquo;t refresh your channel list. {failureDetail(result)}</>);
      return;
    }
    toast.success(result.data.alreadyQueued ? 'Your channel list is already being refreshed.' : 'Refreshing your channel list — it updates here when Slack has answered.');
    setPolls(0);
    request.reload();
  };

  const disconnect = async (): Promise<void> => {
    setDisconnecting(true);
    setDisconnectError(null);
    const result = await api.delete<SlackDisconnectResponse>('/api/connections/slack');
    setDisconnecting(false);
    if (!result.ok) {
      setDisconnectError(result.correlationId ? `${inlineFailureText(result)} (reference ${result.correlationId})` : inlineFailureText(result));
      return;
    }
    setConfirmDisconnect(false);
    if (result.data.revoked) toast.success('Slack disconnected. Calis deleted its copy of your Slack access and everything it read with it.');
    else toast.warning('Slack didn’t confirm the removal. You can also remove Calis in Slack’s app settings. Calis’s copy is deleted.');
    request.reload();
  };

  const view = data ? slackPanelView(data) : null;
  const outcomeBanner = outcome?.kind === 'banner' && !outcomeDismissed ? outcome.notice : null;
  const openDisconnect = (): void => {
    setDisconnectError(null);
    setConfirmDisconnect(true);
  };

  return (
    <Section id="slack-connection" title="Slack" description="Calis can check each meeting against related Slack conversations.">
      {outcomeBanner ? (
        <NoticeBanner notice={outcomeBanner} onDismiss={outcomeBanner.tone === 'info' ? () => setOutcomeDismissed(true) : undefined} />
      ) : null}

      {request.status === 'loading' && !data ? <StateView kind="loading" label="Checking your Slack connection" minHeight="16rem" /> : null}
      {request.status === 'error' && request.error && !data ? (
        <FailureView failure={request.error} what="your Slack connection" onRetry={request.reload} featureName="The Slack integration" minHeight="16rem" />
      ) : null}

      {data && view ? (() => {
        switch (view.state) {
          case 'not-configured':
            return (
              <StateView
                kind="not-configured"
                title="Slack is not switched on in this installation"
                description={
                  <>
                    <p>Nothing here is failing — an operator can switch it on.</p>
                    {data.connection ? (
                      <p className="text-sm muted">Your earlier connection to {data.connection.teamName ?? 'Slack'} is kept but unused until then. Disconnect it to delete what Calis holds.</p>
                    ) : null}
                  </>
                }
                action={view.canDisconnect ? <Button variant="danger-outline" icon="unplug" onClick={openDisconnect}>Disconnect Slack</Button> : undefined}
                minHeight="16rem"
              />
            );
          case 'not-permitted':
            return (
              <StateView
                kind="denied"
                title="Your account cannot connect Slack"
                description={
                  <>
                    <p>Your access doesn’t include Slack connect. Ask an administrator to add you to a group that has it.</p>
                    {view.canDisconnect && data.connection ? (
                      <p className="text-sm muted">
                        Your earlier connection to {data.connection.teamName ?? 'Slack'} is kept, though Slack no longer accepts it. Disconnect it to delete what Calis still holds of it.
                      </p>
                    ) : null}
                  </>
                }
                action={view.canDisconnect ? <Button variant="danger-outline" icon="unplug" onClick={openDisconnect}>Disconnect Slack</Button> : undefined}
                minHeight="16rem"
              />
            );
          case 'not-connected':
            return (
              <>
                <Notices notices={view.notices} />
                <NotConnected data={data} ai={me.integrations.ai} primary={connectIsPrimary} sorting={{ holdsAiSummaries: me.capabilities.aiSummaries, holdsSlackAnalysis: me.capabilities.slackConnect && me.capabilities.slackAnalysis, sortingOn: me.integrations.projects !== 'off' }} />
              </>
            );
          case 'revoked':
            return (
              <>
                <Notices notices={view.notices} />
                <div className="connection-actions">
                  <Button variant="danger-outline" icon="unplug" onClick={openDisconnect} className="connection-actions__danger">Disconnect Slack</Button>
                </div>
              </>
            );
          case 'connected':
          default:
            return data.connection ? (
              <>
                <Notices notices={view.notices} />
                <ConnectedFacts response={data} connection={data.connection} refreshing={request.refreshing} />
                <SlackExclusionList
                  // Why keyed on the refresh: a finished refresh may have changed the list.
                  listKey={`${data.connection.id}:${data.connection.lastSyncCompletedAt ?? 'never'}`}
                  canManage={view.canManage}
                  syncInProgress={data.connection.syncInProgress}
                  refreshing={syncing}
                  onRefresh={() => void refreshList()}
                  sweep={data.sweep}
                />
                <SlackSwitches response={data} connection={data.connection} ai={me.integrations.ai} canManage={view.canManage} onSaved={(response) => request.setData(response)} />
                {/* Destructive, so last and set apart from the regions' own actions (03-components §2). */}
                <div className="connection-actions">
                  <Button variant="danger-outline" icon="unplug" onClick={openDisconnect} className="connection-actions__danger">Disconnect Slack</Button>
                </div>
              </>
            ) : null;
        }
      })() : null}

      <ConfirmDialog
        open={confirmDisconnect}
        title="Disconnect Slack?"
        confirmLabel="Disconnect Slack"
        danger
        busy={disconnecting}
        error={disconnectError}
        onCancel={() => { if (!disconnecting) setConfirmDisconnect(false); }}
        onConfirm={disconnect}
      >
        <p>
          Calis asks Slack to revoke its access, and deletes its token, your channel list, the list of channels Calis won&rsquo;t read, and the Slack
          context it holds for your meetings, dismissed findings included. Your meetings, summaries and to-dos — including any you added from a
          Slack finding — stay.
        </p>
      </ConfirmDialog>
    </Section>
  );
}

// --- Banners -----------------------------------------------------------------------------------

function NoticeBanner({ notice, onDismiss }: { notice: SlackNotice; onDismiss?: () => void }) {
  return (
    <Banner
      tone={notice.tone}
      title={notice.title}
      onDismiss={onDismiss}
      actions={notice.reconnect ? <LinkButton href={SLACK_CONNECT_PATH} variant="secondary" size="sm" icon="plug">Connect Slack again</LinkButton> : undefined}
    >
      <p>{notice.text}</p>
      {notice.code || notice.at ? (
        <p className="text-xs muted">
          {notice.at ? <>Recorded <RelativeTime value={notice.at} mode="both" />. </> : null}
          {notice.code ? <>Code <code className="selectable">{notice.code}</code></> : null}
        </p>
      ) : null}
    </Banner>
  );
}

function Notices({ notices }: { notices: readonly SlackNotice[] }) {
  return notices.length > 0 ? <>{notices.map((notice) => <NoticeBanner key={notice.id} notice={notice} />)}</> : null;
}

// --- Not connected -----------------------------------------------------------------------------

function NotConnected({ data, ai, primary, sorting }: { data: SlackConnectionResponse; ai: Parameters<typeof slackDisclosure>[0]['ai']; primary: boolean; sorting: NonNullable<Parameters<typeof slackDisclosure>[0]['sorting']> }) {
  // Why the reader's facts (owner ruling 11's Q1(c) and Q13; design §2.11): for a reader without AI summaries the Slack
  // permissions carry the sorting pass over every to-do of theirs, so it is said here, before they connect.
  // The climate clause (ADR 0019; design §2.14 item 7) while executive intelligence is on here — selection only.
  const climate = useMe().integrations.executive === 'on';
  const disclosure = slackDisclosure({ conversationTypes: data.conversationTypes, followUpHours: data.followUpHours, retentionDays: data.retentionDays, ai, sweep: data.sweep, sweepMeetingDays: data.sweepMeetingDays, sorting, climate });
  return (
    <div className="sheet stack stack--3">
      <div className="settings-prose">
        <p>Before you connect:</p>
        <ul>
          <li><strong>What Calis may read:</strong> {disclosure.reads}.</li>
          <li><strong>How a check looks:</strong> {disclosure.how}.</li>
          <li><strong>When:</strong> {disclosure.when}.</li>
          <li><strong>What leaves Calis:</strong> {disclosure.leaves}.</li>
          <li><strong>What Calis keeps:</strong> {disclosure.kept}.</li>
        </ul>
        <p className="text-sm muted">Slack asks you to approve read-only access. Calis connects only the Slack account that uses your Calis email address, and never posts anything.</p>
      </div>
      <div className="cluster">
        <LinkButton href={SLACK_CONNECT_PATH} variant={primary ? 'primary' : 'secondary'} icon="plug">Connect Slack</LinkButton>
      </div>
    </div>
  );
}

// --- Connected ---------------------------------------------------------------------------------

function ConnectedFacts({ response, connection, refreshing }: { response: SlackConnectionResponse; connection: SlackConnectionDto; refreshing: boolean }) {
  // Why the most recent refresh's state and not the connection's (06-modules §6.4 State; finding S5): "Active" said the
  // connection stood, not whether its last refresh completed. A connection Slack stopped accepting still shows: as the
  // notice above, and as the connection's own sentence (`slackStatusPresentation`'s detail — never its word, since the
  // pill's word is the run's) after the run's in the pill's title, where there is a run to pill. A revoked connection
  // never reaches these facts: the panel's revoked state, with its "Revoked —" notice, stands in their place.
  const status = slackStatusPresentation(connection);
  const lastRun = lastRunStatePresentation(response.runs, connection.syncInProgress);
  const lastTrigger = response.runs.last ? runTriggerPresentation(response.runs.last.trigger) : null;
  return (
    <>
    <dl className="field-list sheet">
      <dt>State</dt>
      <dd className="connection-run-state">
        {lastRun ? <StatusPill tone={lastRun.tone} icon={lastRun.icon} title={`${lastRun.detail} ${status.detail}`}>{lastRun.word}</StatusPill> : <span className="muted">{NEVER}</span>}
        {/* The time is the time of the run the word describes — never another's: Pending names a run that has not begun. */}
        {lastRun?.run && lastRun.run.state !== 'running' ? <span className="text-sm muted"> of the refresh <RelativeTime value={lastRun.run.startedAt} /></span> : null}
        {refreshing ? <span className="visually-hidden"> (refreshing)</span> : null}
      </dd>
      <dt>Workspace</dt>
      <dd>{connection.teamName ?? connection.teamId}</dd>
      <dt>Slack account</dt>
      <dd>{connection.slackUserName ?? EM_DASH}</dd>
      <dt>Can read</dt>
      <dd>{slackReadableSummary(connection.readableKinds, connection.includeDms, connection.searchGranted)}</dd>
      <dt>Credential</dt>
      <dd>
        <code className="selectable">{maskedKey(connection.credentialFingerprint)}</code>{' '}
        <span className="muted text-xs">(a fingerprint, not the token; the token is stored encrypted)</span>
      </dd>
      <dt>Last success</dt>
      <dd>
        {/* The last channel-list refresh that COMPLETED, never merely the last run; "— never" where there has been none (§6.4, §6.7). */}
        {connection.lastSyncCompletedAt
          ? <>Channel list refreshed <RelativeTime value={connection.lastSyncCompletedAt} mode="both" /></>
          : <span className="muted">{NEVER}</span>}
      </dd>
      <dt>Trigger</dt>
      <dd className="connection-run-trigger" data-trigger={response.runs.last?.trigger ?? 'none'}>
        {/* How the most recent refresh began (§6.4 Trigger) — worded as how it began, never with a state word. */}
        {lastTrigger && response.runs.last ? (
          <>
            <Icon name={lastTrigger.icon} size={14} /> {lastTrigger.word}
            <span className="text-sm muted"> · started <RelativeTime value={response.runs.last.startedAt} mode="both" /></span>
          </>
        ) : <span className="muted">{NEVER}</span>}
      </dd>
      <UnattendedStreakRow summary={response.runs} system="slack" />
      <dt>Last Slack check</dt>
      <dd>{connection.lastSuccessAt ? <RelativeTime value={connection.lastSuccessAt} mode="both" /> : <span className="muted">{NEVER}</span>}</dd>
      <dt>Connected</dt>
      <dd><RelativeTime value={connection.createdAt} mode="both" /></dd>
    </dl>
    {/* The route from the system to its run history (06-modules §6.3 R2, §6.5), named with its system since the Fellow
        panel on this page offers the same. */}
    <div className="cluster">
      <LinkButton to={connectionRunsPath('slack')} variant="ghost" icon="list">Recent Slack refreshes</LinkButton>
    </div>
    </>
  );
}

export function SlackSwitches({ response, connection, ai, canManage, onSaved }: {
  response: SlackConnectionResponse;
  connection: SlackConnectionDto;
  ai: Parameters<typeof slackDisclosure>[0]['ai'];
  canManage: boolean;
  onSaved: (response: SlackConnectionResponse) => void;
}) {
  const toast = useToast();
  const [autoCheck, setAutoCheck] = useState(connection.autoCheck);
  const [includeDms, setIncludeDms] = useState(connection.includeDms);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // What the server holds wins whenever it changes (a save, a reload after a refresh).
  useEffect(() => {
    setAutoCheck(connection.autoCheck);
    setIncludeDms(connection.includeDms);
  }, [connection.autoCheck, connection.includeDms]);
  const readsDms = response.conversationTypes.some((kind) => kind === 'im' || kind === 'mpim');

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const body: PatchSlackConnectionRequest = {};
    if (autoCheck !== connection.autoCheck) body.autoCheck = autoCheck;
    if (readsDms && includeDms !== connection.includeDms) body.includeDms = includeDms;
    if (body.autoCheck === undefined && body.includeDms === undefined) {
      toast.info('Your settings are unchanged.');
      return;
    }
    setSaving(true);
    setError(null);
    const result = await api.patch<SlackConnectionResponse>('/api/connections/slack', body);
    setSaving(false);
    if (!result.ok) {
      setError(result.correlationId ? `${inlineFailureText(result)} (reference ${result.correlationId})` : inlineFailureText(result));
      return;
    }
    onSaved(result.data);
    toast.success(body.includeDms === false ? 'Settings saved. Calis no longer reads your direct messages or group chats and has forgotten their list.' : 'Settings saved.');
  };

  return (
    <section className="sheet stack stack--3" aria-labelledby="slack-switches-heading">
      <h3 id="slack-switches-heading">Automatic checks, direct messages and group chats</h3>
      <form className="stack stack--3" onSubmit={(event) => void submit(event)} noValidate>
        {/* Why two disables, not one (app-baseline 05-client §5.5; AB-Q7 of 2026-10-05): the fieldset holds the switches
            only while a save is in flight, which the Save's loading state already says; a switch the person may not
            change is disabled on its own, carrying the reason on hover, as its accessible description and — being
            aria-disabled and still focusable (the owner's ruling J-D3 of 2026-10-06) — by keyboard. */}
        <fieldset className="fieldset" disabled={saving}>
          <legend className="visually-hidden">Automatic checks, direct messages and group chats</legend>
          <div className="stack stack--2">
            <Checkbox
              checked={autoCheck}
              disabled={!canManage}
              disabledReason={canManage ? undefined : SLACK_SETTINGS_LOCKED_REASON}
              onChange={(event) => setAutoCheck(event.target.checked)}
              label={autoCheckLabel(response.followUpHours, response.sweep)}
              description={[
                `For meetings from the last ${autoProcessWindowLabel()}. Calis makes at most ${response.runsPerDay} Slack checks a day for you, counting the ones you start.`,
                dailyLookNote(response.sweep, ai, response.sweepMeetingDays),
              ]
                .filter((sentence): sentence is string => sentence !== null)
                .join(' ')}
            />
            {readsDms ? (
              <Checkbox
                checked={includeDms}
                disabled={!canManage}
                disabledReason={canManage ? undefined : SLACK_SETTINGS_LOCKED_REASON}
                onChange={(event) => setIncludeDms(event.target.checked)}
                label="Include direct messages and group chats"
                description={['All of them, those with people outside Centro too; the ones with a meeting’s attendees are read first. Switched off, Calis never reads, keeps or sends any of them — a search match in one is dropped too — and forgets their list.', sweepWithdrawalNote(response.sweep, 'dms')]
                  .filter((sentence): sentence is string => sentence !== null)
                  .join(' ')}
              />
            ) : null}
          </div>
        </fieldset>
        {error ? <p className="field__error" role="alert">{error}</p> : null}
        {canManage ? (
          <div className="cluster">
            <Button type="submit" variant="secondary" icon="check" loading={saving}>Save settings</Button>
          </div>
        ) : null}
      </form>
    </section>
  );
}
